import React, { useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import demos from '../../data/demos';
import useSeo from '../../hooks/useSeo';
import useDemoFonts from '../../hooks/useDemoFonts';
import DemoTemplate from './DemoTemplate';
import ApexPlumbingDemo from './custom/ApexPlumbingDemo';
import BlueDoorBakeryDemo from './custom/BlueDoorBakeryDemo';
import WildStemFloralDemo from './custom/WildStemFloralDemo';
import RiversideDentalDemo from './custom/RiversideDentalDemo';
import GreenLeafLandscapingDemo from './custom/GreenLeafLandscapingDemo';
import UrbanFitnessDemo from './custom/UrbanFitnessDemo';

// Per-slug overrides bypass the shared template. Keep entries here while
// rebuilding bespoke per-business pages.
const CUSTOM_DEMOS = {
  'apex-plumbing': ApexPlumbingDemo,
  'bakery': BlueDoorBakeryDemo,
  'wild-stem': WildStemFloralDemo,
  'riverside-dental': RiversideDentalDemo,
  'greenleaf-landscaping': GreenLeafLandscapingDemo,
  'urban-fitness': UrbanFitnessDemo,
};

// Public showcase route: renders one of the baked-in sample demos by slug.
function DemoPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const demo = demos.find((d) => d.slug === slug);

  useDemoFonts();
  useSeo({
    title: demo
      ? `${demo.name} — Website Demo by Sudco Solutions`
      : 'Website Demo — Sudco Solutions',
    description: demo
      ? `${demo.tagline} A live website demo for ${demo.name} (${demo.industry}), built by Sudco Solutions — $800 flat.`
      : 'A live small-business website demo, built by Sudco Solutions.',
    path: demo ? `/demos/${demo.slug}` : '/demos',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Track cold-email clicks: /demos/:slug?from=<prospect_id> POSTs a single
  // hit so we can see who actually opened their demo. Fire-and-forget; failures
  // don't affect rendering. Skipped when no `from` is present.
  useEffect(() => {
    if (!demo) return;
    const from = searchParams.get('from');
    if (!from) return;
    fetch('/api/showcase_views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prospect_id: from, demo_slug: demo.slug }),
      keepalive: true,
    }).catch(() => {});
  }, [demo, searchParams]);

  if (!demo) return <Navigate to="/" replace />;
  const Custom = CUSTOM_DEMOS[demo.slug];
  if (Custom) return <Custom demo={demo} sample />;
  return <DemoTemplate demo={demo} sample />;
}

export default DemoPage;
